import { DRIVER_LICENSE_TYPES, SINGLE_UPLOAD_ID_TYPES } from '@common/constants/contactFields';
import { DEFAULT_TEMPLATE_FORM_LANGUAGE } from '@common/i18n/constants';
import {
    buildSelectOptions,
    CITIZENSHIP_STATUS_CANONICAL_VALUES,
    GENDER_CANONICAL_VALUES,
    getContactFieldLabel,
    getContactInfoTranslations,
    getContactSectionTitle,
    ID_TYPE_CANONICAL_VALUES,
    MARITAL_STATUS_CANONICAL_VALUES,
} from '@common/i18n/contactInfo';
import type { ContactFormValues } from '@common/types/formSubmissions';
import type {
    ContactBlockHiddenFields,
    OptionalCoreContactFields,
} from '@common/types/templateFormFields';
import type {
    ContactFieldLabelKey,
    ContactFieldLabels,
    RequiredContactFields,
    TemplateFormLanguage,
} from '@common/types/templateForms';
import { parseDateOfBirth } from '@common/utils/contactFormValues';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    FormControl,
    FormHelperText,
    MenuItem,
    Select,
    type SelectChangeEvent,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { type ReactNode, useMemo } from 'react';
import FormUploadField from '@/components/FormComponent/FormUploadField';
import GooglePlacesAutocomplete from '@/components/GooglePlacesAutocomplete';
import RequiredAsterisk from '@/components/RequiredAsterisk';
import { deleteFile } from '@/services/FileService';
import SectionDescription from './SectionDescription';

const UPLOAD_VALIDATION = {
    fileSize: 10 * 1024 * 1024, // 10MB
    fileCount: 1,
    fileType: [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/png',
        'image/jpeg',
    ],
};

interface ContactInfoSectionProps {
    index: number;
    totalContacts: number;
    /** Overrides the default "Your contact information" / ordinal title. */
    title?: string;
    values: ContactFormValues;
    errors?: Partial<Record<keyof ContactFormValues, string>>;
    onChange: (field: keyof ContactFormValues, value: unknown) => void;
    lawFirmId?: string;
    disabledFields?: Partial<Record<keyof ContactFormValues, boolean>>;
    defaultExpanded?: boolean;
    expanded?: boolean;
    onExpandedChange?: (expanded: boolean) => void;
    /** Skips every contact field, leaving the header, description and children. */
    hideContactFields?: boolean;
    hiddenFields?: ContactBlockHiddenFields;
    requiredFields?: RequiredContactFields;
    /** Email and cell phone are required unless marked optional here. */
    optionalCoreFields?: OptionalCoreContactFields;
    fieldLabels?: ContactFieldLabels;
    description?: string | null;
    language?: TemplateFormLanguage;
    /** Rendered at the end of the section, e.g. nested block questions. */
    children?: ReactNode;
    /** Rendered in the section header next to the expand icon, e.g. a remove button. */
    headerAction?: ReactNode;
}

export default function ContactInfoSection({
    index,
    totalContacts,
    title,
    values,
    errors = {},
    onChange,
    lawFirmId,
    disabledFields = {},
    defaultExpanded = true,
    expanded,
    onExpandedChange,
    hideContactFields = false,
    hiddenFields = {},
    requiredFields = {},
    optionalCoreFields = {},
    fieldLabels = {},
    description,
    language = DEFAULT_TEMPLATE_FORM_LANGUAGE,
    children,
    headerAction,
}: ContactInfoSectionProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const t = getContactInfoTranslations(language);
    const getLabel = (field: ContactFieldLabelKey) => getContactFieldLabel(field, t, fieldLabels);

    const sectionTitle = title ?? getContactSectionTitle(index, totalContacts, language);

    const idTypeOptions = useMemo(
        () => buildSelectOptions(ID_TYPE_CANONICAL_VALUES, t.idTypes, t.placeholders.selectIdType),
        [t],
    );

    const maritalStatusOptions = useMemo(
        () =>
            buildSelectOptions(
                MARITAL_STATUS_CANONICAL_VALUES,
                t.maritalStatuses,
                t.placeholders.selectMaritalStatus,
            ),
        [t],
    );

    const citizenshipStatusOptions = useMemo(
        () =>
            buildSelectOptions(
                CITIZENSHIP_STATUS_CANONICAL_VALUES,
                t.citizenshipStatuses,
                t.placeholders.selectCitizenshipStatus,
            ),
        [t],
    );

    const genderOptions = useMemo(
        () => buildSelectOptions(GENDER_CANONICAL_VALUES, t.genders, t.placeholders.selectGender),
        [t],
    );

    const firstIdRequiresDoubleUpload = DRIVER_LICENSE_TYPES.includes(values.firstIdType ?? '');
    const firstIdRequiresSingleUpload = SINGLE_UPLOAD_ID_TYPES.includes(values.firstIdType ?? '');
    const secondIdRequiresDoubleUpload = DRIVER_LICENSE_TYPES.includes(values.secondIdType ?? '');
    const secondIdRequiresSingleUpload = SINGLE_UPLOAD_ID_TYPES.includes(values.secondIdType ?? '');

    const handleIdTypeChange = (idTypeField: 'firstIdType' | 'secondIdType', newValue: string) => {
        const uploadFields: (keyof ContactFormValues)[] =
            idTypeField === 'firstIdType'
                ? ['firstIdFront', 'firstIdBack', 'firstIdSingle']
                : ['secondIdFront', 'secondIdBack', 'secondIdSingle'];

        for (const field of uploadFields) {
            const fileId = values[field] as string | undefined;
            if (fileId) {
                deleteFile(fileId);
                onChange(field, undefined);
            }
        }

        onChange(idTypeField, newValue);
    };

    const textFieldSx = {
        '& .MuiOutlinedInput-input': { padding: '8px 12px' },
        '& .MuiFormHelperText-root': { marginLeft: 0 },
    };

    const selectSx = {
        '& .MuiSelect-select': { padding: '8px 12px' },
    };

    const rowSx = {
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 2 : 3,
    };

    const uploadRowSx = {
        flexDirection: isMobile ? 'column' : 'row',
        flexWrap: 'wrap',
        gap: 2,
    };

    const uploadFieldSx = {
        flex: isMobile ? '1 1 100%' : '1 1 calc(50% - 8px)',
        minWidth: isMobile ? '100%' : '240px',
    };

    const renderLabel = (label: string, required = false) => (
        <Typography variant={isMobile ? 'body2' : 'body1'} sx={{ mb: 0.5 }}>
            {required && <RequiredAsterisk />}
            {label}
        </Typography>
    );

    const hasVisibleField = (fields: (keyof ContactFormValues)[]) =>
        fields.some((field) => !hiddenFields[field]);

    const renderTextField = (
        field: keyof ContactFormValues,
        label: string,
        required = false,
        placeholder?: string,
        autoComplete?: string,
    ) => {
        if (!required && hiddenFields[field]) return null;

        return (
            <Box sx={{ flex: 1, minWidth: isMobile ? '100%' : 0 }}>
                {renderLabel(label, required)}
                <TextField
                    fullWidth
                    size="small"
                    sx={textFieldSx}
                    placeholder={placeholder}
                    value={values[field] ?? ''}
                    error={!!errors[field]}
                    helperText={errors[field]}
                    disabled={disabledFields[field]}
                    autoComplete={autoComplete}
                    // The label above is plain text, so name the input for screen readers
                    inputProps={{ 'aria-label': label }}
                    onChange={(e) => onChange(field, e.target.value)}
                />
            </Box>
        );
    };

    const renderAddressField = (
        field: keyof ContactFormValues,
        label: string,
        required = false,
        placeholder?: string,
    ) => {
        if (!required && hiddenFields[field]) return null;

        return (
            <Box sx={{ flex: 1, minWidth: isMobile ? '100%' : 0 }}>
                {renderLabel(label, required)}
                <GooglePlacesAutocomplete
                    name={field}
                    value={(values[field] as string | undefined) ?? ''}
                    isError={!!errors[field]}
                    helperText={errors[field]}
                    disabled={disabledFields[field]}
                    placeholder={placeholder}
                    language={language}
                    onChange={(value) => onChange(field, value)}
                />
            </Box>
        );
    };

    const renderSelect = (
        field: keyof ContactFormValues,
        label: string,
        options: { value: string; label: string }[],
        required = false,
        autoComplete?: string,
    ) => {
        if (!required && hiddenFields[field]) return null;

        return (
            <Box sx={{ flex: 1, minWidth: isMobile ? '100%' : 0 }}>
                {renderLabel(label, required)}
                <FormControl fullWidth error={!!errors[field]}>
                    <Select
                        size="small"
                        sx={selectSx}
                        value={values[field] ?? ''}
                        disabled={disabledFields[field]}
                        displayEmpty
                        autoComplete={autoComplete}
                        onChange={(e: SelectChangeEvent) => onChange(field, e.target.value)}
                    >
                        {options.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value} disabled={opt.value === ''}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                    {errors[field] && <FormHelperText>{errors[field]}</FormHelperText>}
                </FormControl>
            </Box>
        );
    };

    const renderUploadField = (
        field: keyof ContactFormValues,
        label: string,
        typeOfIdValue?: string,
        uploadDescription?: string,
        required = false,
    ) => {
        if (hiddenFields[field]) return null;

        const fileId = values[field] as string | undefined;
        const fileIds = fileId ? [fileId] : undefined;

        return (
            <Box sx={uploadFieldSx}>
                {renderLabel(label, required)}
                {uploadDescription && (
                    <Typography variant="body2" sx={{ color: theme.palette.neutral.N_500 }}>
                        {uploadDescription}
                    </Typography>
                )}
                <FormUploadField
                    name={field}
                    language={language}
                    fileIds={fileIds}
                    selectedLawFirmId={lawFirmId}
                    typeofDocument="ID"
                    typeOfIdValue={typeOfIdValue}
                    validationSchema={UPLOAD_VALIDATION}
                    isError={!!errors[field]}
                    disabled={disabledFields[field]}
                    onFileIdsChange={(ids) => onChange(field, ids[0])}
                />
                {errors[field] && (
                    <FormHelperText error sx={{ ml: 0 }}>
                        {errors[field]}
                    </FormHelperText>
                )}
            </Box>
        );
    };

    return (
        <Accordion
            {...(expanded !== undefined
                ? { expanded, onChange: (_event, isExpanded) => onExpandedChange?.(isExpanded) }
                : { defaultExpanded })}
            sx={{
                border: `1px solid ${theme.palette.neutral.borderColor}`,
                backgroundColor: theme.palette.neutral.white,
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                    px: isMobile ? 2 : 3,
                    backgroundColor: theme.palette.primary.P_50,
                }}
            >
                <Stack
                    direction="row"
                    sx={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        pr: 1,
                    }}
                >
                    <Typography variant={isMobile ? 'h4' : 'h3'}>{sectionTitle}</Typography>
                    {headerAction}
                </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ px: isMobile ? 2 : 3, pb: isMobile ? 2 : 3 }}>
                <Stack spacing={3}>
                    {description && (
                        <SectionDescription description={description} sx={{ marginTop: 0 }} />
                    )}
                    {!hideContactFields && (
                        <>
                            {(!hiddenFields.firstId || !hiddenFields.secondId) && (
                                <Stack sx={rowSx}>
                                    {!hiddenFields.firstId && (
                                        <Stack sx={{ width: '100%' }} spacing={2}>
                                            <Box sx={{ flex: 1, minWidth: isMobile ? '100%' : 0 }}>
                                                {renderLabel(
                                                    getLabel('firstIdType'),
                                                    !!requiredFields.firstId,
                                                )}
                                                <FormControl fullWidth error={!!errors.firstIdType}>
                                                    <Select
                                                        size="small"
                                                        sx={selectSx}
                                                        value={values.firstIdType ?? ''}
                                                        disabled={disabledFields.firstIdType}
                                                        displayEmpty
                                                        onChange={(e: SelectChangeEvent) =>
                                                            handleIdTypeChange(
                                                                'firstIdType',
                                                                e.target.value,
                                                            )
                                                        }
                                                    >
                                                        {idTypeOptions.map((opt) => (
                                                            <MenuItem
                                                                key={opt.value}
                                                                value={opt.value}
                                                                disabled={opt.value === ''}
                                                            >
                                                                {opt.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    {errors.firstIdType && (
                                                        <FormHelperText>
                                                            {errors.firstIdType}
                                                        </FormHelperText>
                                                    )}
                                                </FormControl>
                                            </Box>
                                            {firstIdRequiresDoubleUpload && (
                                                <Stack sx={uploadRowSx}>
                                                    {renderUploadField(
                                                        'firstIdFront',
                                                        getLabel('firstIdFront'),
                                                        values.firstIdType,
                                                        t.uploadDescriptions.front,
                                                        !!requiredFields.firstId,
                                                    )}
                                                    {renderUploadField(
                                                        'firstIdBack',
                                                        getLabel('firstIdBack'),
                                                        values.firstIdType,
                                                        t.uploadDescriptions.back,
                                                        !!requiredFields.firstId,
                                                    )}
                                                </Stack>
                                            )}
                                            {firstIdRequiresSingleUpload &&
                                                renderUploadField(
                                                    'firstIdSingle',
                                                    getLabel('firstIdSingle'),
                                                    values.firstIdType,
                                                    t.uploadDescriptions.single,
                                                    !!requiredFields.firstId,
                                                )}
                                        </Stack>
                                    )}
                                    {!hiddenFields.secondId && (
                                        <Stack sx={{ width: '100%' }} spacing={2}>
                                            <Box sx={{ flex: 1, minWidth: isMobile ? '100%' : 0 }}>
                                                {renderLabel(
                                                    getLabel('secondIdType'),
                                                    !!requiredFields.secondId,
                                                )}
                                                <FormControl
                                                    fullWidth
                                                    error={!!errors.secondIdType}
                                                >
                                                    <Select
                                                        size="small"
                                                        sx={selectSx}
                                                        value={values.secondIdType ?? ''}
                                                        disabled={disabledFields.secondIdType}
                                                        displayEmpty
                                                        onChange={(e: SelectChangeEvent) =>
                                                            handleIdTypeChange(
                                                                'secondIdType',
                                                                e.target.value,
                                                            )
                                                        }
                                                    >
                                                        {idTypeOptions.map((opt) => (
                                                            <MenuItem
                                                                key={opt.value}
                                                                value={opt.value}
                                                                disabled={opt.value === ''}
                                                            >
                                                                {opt.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    {errors.secondIdType && (
                                                        <FormHelperText>
                                                            {errors.secondIdType}
                                                        </FormHelperText>
                                                    )}
                                                </FormControl>
                                            </Box>
                                            {secondIdRequiresDoubleUpload && (
                                                <Stack sx={uploadRowSx}>
                                                    {renderUploadField(
                                                        'secondIdFront',
                                                        getLabel('secondIdFront'),
                                                        values.secondIdType,
                                                        t.uploadDescriptions.front,
                                                        !!requiredFields.secondId,
                                                    )}
                                                    {renderUploadField(
                                                        'secondIdBack',
                                                        getLabel('secondIdBack'),
                                                        values.secondIdType,
                                                        t.uploadDescriptions.back,
                                                        !!requiredFields.secondId,
                                                    )}
                                                </Stack>
                                            )}
                                            {secondIdRequiresSingleUpload &&
                                                renderUploadField(
                                                    'secondIdSingle',
                                                    getLabel('secondIdSingle'),
                                                    values.secondIdType,
                                                    t.uploadDescriptions.single,
                                                    !!requiredFields.secondId,
                                                )}
                                        </Stack>
                                    )}
                                </Stack>
                            )}

                            <Stack sx={rowSx}>
                                {renderTextField(
                                    'firstName',
                                    getLabel('firstName'),
                                    true,
                                    undefined,
                                    'given-name',
                                )}
                                {renderTextField(
                                    'middleName',
                                    getLabel('middleName'),
                                    !!requiredFields.middleName,
                                    undefined,
                                    'additional-name',
                                )}
                                {renderTextField(
                                    'lastName',
                                    getLabel('lastName'),
                                    true,
                                    undefined,
                                    'family-name',
                                )}
                            </Stack>

                            {hasVisibleField(['email', 'cellPhone', 'homePhone']) && (
                                <Stack sx={rowSx}>
                                    {renderTextField(
                                        'email',
                                        getLabel('email'),
                                        !optionalCoreFields.email,
                                        undefined,
                                        'email',
                                    )}
                                    {renderTextField(
                                        'cellPhone',
                                        getLabel('cellPhone'),
                                        !optionalCoreFields.cellPhone,
                                        undefined,
                                        'tel',
                                    )}
                                    {renderTextField(
                                        'homePhone',
                                        getLabel('homePhone'),
                                        !!requiredFields.homePhone,
                                        undefined,
                                        'tel',
                                    )}
                                </Stack>
                            )}

                            {hasVisibleField(['homeAddress', 'homeUnit']) && (
                                <Stack sx={rowSx}>
                                    {renderAddressField(
                                        'homeAddress',
                                        getLabel('homeAddress'),
                                        !!requiredFields.homeAddress,
                                    )}
                                    {renderTextField(
                                        'homeUnit',
                                        getLabel('homeUnit'),
                                        !!requiredFields.homeUnit,
                                        undefined,
                                        'address-line2',
                                    )}
                                </Stack>
                            )}

                            {hasVisibleField([
                                'dateOfBirth',
                                'maritalStatus',
                                'citizenshipStatus',
                            ]) && (
                                <Stack sx={rowSx}>
                                    {!hiddenFields.dateOfBirth && (
                                        <Box
                                            sx={{
                                                flex: 1,
                                                minWidth: isMobile ? '100%' : 0,
                                                maxWidth: isMobile ? '100%' : '50%',
                                            }}
                                        >
                                            {renderLabel(
                                                getLabel('dateOfBirth'),
                                                !!requiredFields.dateOfBirth,
                                            )}
                                            <LocalizationProvider dateAdapter={AdapterLuxon}>
                                                <DatePicker
                                                    value={parseDateOfBirth(values.dateOfBirth)}
                                                    disabled={disabledFields.dateOfBirth}
                                                    sx={{
                                                        width: '100%',
                                                        '& .MuiOutlinedInput-input': {
                                                            padding: '8px 12px',
                                                        },
                                                    }}
                                                    slotProps={{
                                                        textField: {
                                                            size: 'small',
                                                            error: !!errors.dateOfBirth,
                                                            helperText: errors.dateOfBirth,
                                                            autoComplete: 'bday',
                                                        },
                                                    }}
                                                    onChange={(newValue) =>
                                                        onChange(
                                                            'dateOfBirth',
                                                            // A half-typed date stays invalid rather than empty;
                                                            // the picker clears what was typed if it sees null
                                                            newValue?.isValid
                                                                ? newValue.toISODate()
                                                                : newValue?.toString(),
                                                        )
                                                    }
                                                />
                                            </LocalizationProvider>
                                        </Box>
                                    )}
                                    {renderSelect(
                                        'maritalStatus',
                                        getLabel('maritalStatus'),
                                        maritalStatusOptions,
                                        !!requiredFields.maritalStatus,
                                    )}
                                    {renderSelect(
                                        'citizenshipStatus',
                                        getLabel('citizenshipStatus'),
                                        citizenshipStatusOptions,
                                        !!requiredFields.citizenshipStatus,
                                    )}
                                </Stack>
                            )}

                            {hasVisibleField(['gender', 'preferredPronouns']) && (
                                <Stack sx={rowSx}>
                                    {renderSelect(
                                        'gender',
                                        getLabel('gender'),
                                        genderOptions,
                                        !!requiredFields.gender,
                                        'sex',
                                    )}
                                    {renderTextField(
                                        'preferredPronouns',
                                        getLabel('preferredPronouns'),
                                        !!requiredFields.preferredPronouns,
                                        t.placeholders.preferredPronouns,
                                    )}
                                </Stack>
                            )}

                            {hasVisibleField([
                                'occupation',
                                'employer',
                                'employerAddress',
                                'employerUnit',
                                'employerPhone',
                            ]) && (
                                <>
                                    {hasVisibleField(['occupation', 'employer']) && (
                                        <Stack sx={rowSx}>
                                            {renderTextField(
                                                'occupation',
                                                getLabel('occupation'),
                                                !!requiredFields.occupation,
                                                undefined,
                                                'organization-title',
                                            )}
                                            {renderTextField(
                                                'employer',
                                                getLabel('employer'),
                                                !!requiredFields.employer,
                                                undefined,
                                                'organization',
                                            )}
                                        </Stack>
                                    )}

                                    {hasVisibleField([
                                        'employerAddress',
                                        'employerUnit',
                                        'employerPhone',
                                    ]) && (
                                        <Stack sx={rowSx}>
                                            {renderAddressField(
                                                'employerAddress',
                                                getLabel('employerAddress'),
                                                !!requiredFields.employerAddress,
                                            )}
                                            {renderTextField(
                                                'employerUnit',
                                                getLabel('employerUnit'),
                                                !!requiredFields.employerUnit,
                                                undefined,
                                                'work address-line2',
                                            )}
                                            {renderTextField(
                                                'employerPhone',
                                                getLabel('employerPhone'),
                                                !!requiredFields.employerPhone,
                                                undefined,
                                                'work tel',
                                            )}
                                        </Stack>
                                    )}
                                </>
                            )}
                        </>
                    )}
                    {children}
                </Stack>
            </AccordionDetails>
        </Accordion>
    );
}
